import { cn } from "@shared/utils/cn";
import type { StatBandStat } from "@solomon-labs/design/components/layout";
import { Text } from "@solomon-labs/design/components/ui";
import React from "react";

interface ReservesSummaryProps {
    stats: StatBandStat[];
}

/**
 * The summary figures, with the middle stat's content group centered in its
 * cell but left-aligned internally, and the backing stat aligned to the right
 * on wide screens, with no rules between them.
 *
 * Rules above and below, cells spread evenly, no vertical divider between
 * them. Matches the distributions summary on the Yield tab.
 *
 * Not `StatBand`: that component is the bounded cell group, so it owns a
 * vertical rule between cells and derives alignment from the content rather
 * than taking it as a prop. The brackets here are the ones this layout wants
 * anyway, but without the vertical rule they are bracketing a plain row, not
 * terminating anything, which is not what a StatBand is for.
 *
 * `StatBandStat` is still the shape, so the cells stay swappable if this ever
 * wants to be a band again.
 */
export function ReservesSummary({
    stats
}: ReservesSummaryProps): React.ReactElement {
    return (
        <div className="flex flex-col sm:flex-row border-y border-line-base">
            {stats.map((stat, index) => (
                <div
                    key={stat.label}
                    className={cn(
                        "flex-1 flex flex-col py-4 sm:py-5",
                        index === 1 && stats.length === 3 && "sm:items-center",
                        index === stats.length - 1 &&
                            stats.length > 1 &&
                            "sm:items-end"
                    )}
                >
                    <div
                        className={cn(
                            "flex flex-col gap-1",
                            index === stats.length - 1 &&
                                stats.length > 1 &&
                                "sm:items-end sm:text-right"
                        )}
                    >
                        <Text
                            variant="small"
                            render={<span />}
                            className="text-foreground-muted"
                        >
                            {stat.label}
                        </Text>
                        <Text variant="number-lg" render={<span />}>
                            {stat.value}
                        </Text>
                        {stat.detail === undefined ? null : (
                            <Text
                                variant="fine-print"
                                render={<span />}
                                className="text-foreground-neutral"
                            >
                                {stat.detail}
                            </Text>
                        )}
                    </div>
                </div>
            ))}
        </div>
    );
}
